// ADR 24.9.26h §6 — a shared plan's capability token never leaves in a
// telemetry URL.
//
// `/s/<token>` is a read grant. A consented reader would otherwise send it to
// PostHog in `$current_url` / `$pathname` (default pageview capture), in
// autocapture's `$elements_chain` (a clicked link's `href`, the owner's
// copy-link `text`) and in session properties such as `$session_entry_url`;
// and to Sentry in `request.url`, the `Referer` header and navigation / fetch
// breadcrumbs. A property list would miss the next SDK property to carry a
// URL, so the hooks below walk **every string value** instead of naming any.
//
// Not heuristic: only a path segment directly after `/s/` at the start of a
// path is replaced, so `/settings/s/x` and every other string are untouched,
// and `route_not_found`'s by-path panel still sees `/s/:token/x`.

/**
 * A share path starts at the start of the string, after a quote or `=`
 * (`href="/s/…"`), or after an origin anywhere (`text="https://…/s/abc"`). The
 * token stops at `/ ? # " ' & ;` or whitespace, so a match inside a longer
 * string never runs past its closing quote, and a query or fragment is kept.
 */
const SHARE_PATH = /(^|["'=]|https?:\/\/[^/\s"']+)\/s\/[^/?#\s"'&;]+/g

/** Replace every `/s/<token>` in `url` with `/s/:token`; any other string is returned unchanged. */
export function redactSharePath(url: string): string {
  return url.replace(SHARE_PATH, '$1/s/:token')
}

/**
 * Backend ADR 24.9.26ab D6 — the email-preferences page's `?t=` is a bearer
 * credential too: it reads and flips one learner's lifecycle-mail setting. The
 * `t` parameter on `/email-preferences` — and on the API paths the page calls,
 * `/email/preferences` and `/email/unsubscribe`, which Sentry's fetch
 * breadcrumbs record — is replaced up to the next `& # " '`, whitespace or `;`,
 * whether it comes first or after another parameter. A `t` anywhere else, and
 * every other parameter, is untouched.
 *
 * Two owners since Generator ADR 1.10.26c §7: the trial mails' `/upgrade?t=`
 * is the same kind of credential (it opens a Checkout for one learner) and
 * joins the same pattern. The names stay 24.9.26ab's — its own test imports
 * them. The upgrade route's token rides in a POST body, which neither SDK
 * records, so only the page URL needs this.
 */
const EMAIL_PREFS_TOKEN =
  /(\/(?:email(?:-preferences|\/preferences|\/unsubscribe)|upgrade)\?(?:[^#\s"'?]*&)?t=)[^&#\s"';]+/g

/** Replace the `t` value on the email-preferences and upgrade page and API paths in `url` with `:token`. */
export function redactEmailPrefsToken(url: string): string {
  return url.replace(EMAIL_PREFS_TOKEN, '$1:token')
}

/**
 * Every string inside plain objects and arrays, redacted; anything else (a
 * `Date`, a DOM node, a number) is returned as-is. A new object is built only
 * where something changed, so an event with no token URL is passed through.
 */
function redactStrings<T>(value: T): T {
  if (typeof value === 'string') return redactEmailPrefsToken(redactSharePath(value)) as T
  if (Array.isArray(value)) {
    const mapped = value.map((item: unknown) => redactStrings(item))
    return (mapped.some((item, i) => item !== value[i]) ? mapped : value) as T
  }
  if (value === null || typeof value !== 'object' || Object.getPrototypeOf(value) !== Object.prototype) {
    return value
  }
  let changed = false
  const entries = Object.entries(value as Record<string, unknown>).map(([key, item]) => {
    const next = redactStrings(item)
    if (next !== item) changed = true
    return [key, next] as const
  })
  return (changed ? Object.fromEntries(entries) : value) as T
}

/** The slice of PostHog's `CaptureResult` the hook reads. */
interface PostHogEventLike {
  properties?: Record<string, unknown>
  $set?: Record<string, unknown>
  $set_once?: Record<string, unknown>
}

/**
 * PostHog `before_send`: the event's `properties` and the person updates
 * `$set` / `$set_once` (which carry `$initial_current_url` /
 * `$initial_pathname`). Never drops an event.
 */
export function redactPostHogEvent<E extends PostHogEventLike>(event: E | null): E | null {
  if (event === null) return null
  const redacted = { ...event }
  // Only the keys the event carries, so an absent `$set` is not sent as a key.
  for (const key of ['properties', '$set', '$set_once'] as const) {
    if (event[key] !== undefined) redacted[key] = redactStrings(event[key])
  }
  return redacted
}

/** Sentry `beforeSend` / `beforeBreadcrumb`: every string in the event or breadcrumb. */
export function redactSentryPayload<P>(payload: P): P {
  return redactStrings(payload)
}
